import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AccessModel } from '@jwp/ott-common/types/config';
import type { Customer } from '@jwp/ott-common/types/account';
import type { Offer } from '@jwp/ott-common/types/checkout';
import type { PaymentDetail, Subscription, Transaction } from '@jwp/ott-common/types/subscription';
import { cleengFormatPrice, formatLocalizedDate } from '@jwp/ott-common/src/utils/formatting';
import useBreakpoint from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import useOpaqueId from '@jwp/ott-hooks-react/src/useOpaqueId';
import SubscriptionBox from '../../../../../packages/theme/assets/icons/SubscriptionBox';
import Alert from '../Alert/Alert';
import Button from '../Button/Button';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import OfferSwitch from '../OfferSwitch/OfferSwitch';
import { modalURLFromLocation } from '../../utils/location';
import { useTheme } from '../../../../../ThemeContext';

import styles from './Payment.module.scss';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import TextField from '../TextField/TextField';
import EditCardDetails from '../../containers/AccountModal/forms/EditCardDetails';
import classNames from 'classnames';

const VISIBLE_TRANSACTIONS = 4;

type Props = {
  accessModel: AccessModel;
  activeSubscription: Subscription | null;
  allSubscriptions : Subscription[] | null | undefined;
  activePaymentDetail: PaymentDetail | null;
  transactions: Transaction[] | null;
  customer: Customer;
  pendingOffer: Offer | null;
  isLoading: boolean;
  offerSwitchesAvailable: boolean;
  onShowReceiptClick: (transactionId: string) => void;
  panelClassName?: string;
  panelHeaderClassName?: string;
  onShowAllTransactionsClick?: () => void;
  onUpgradeSubscriptionClick?: () => void;
  showAllTransactions: boolean;
  canUpdatePaymentMethod: boolean;
  canRenewSubscription?: boolean;
  canShowReceipts?: boolean;
  offers?: Offer[];
  pendingDowngradeOfferId?: string;
  changeSubscriptionPlan: {
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    reset: () => void;
  };
  onChangePlanClick: () => void;
  selectedOfferId: string | null;
  setSelectedOfferId: (offerId: string | null) => void;
  isUpgradeOffer: boolean | undefined;
  setIsUpgradeOffer: (isUpgradeOffer: boolean | undefined) => void;
};

const Payment = ({
  accessModel,
  activePaymentDetail,
  activeSubscription,
  allSubscriptions,
  pendingOffer,
  transactions,
  customer,
  isLoading,
  panelClassName,
  panelHeaderClassName,
  onShowAllTransactionsClick,
  showAllTransactions,
  onShowReceiptClick,
  canRenewSubscription = false,
  canShowReceipts = false,
  canUpdatePaymentMethod,
  onUpgradeSubscriptionClick,
  offerSwitchesAvailable,
  offers = [],
  pendingDowngradeOfferId = '',
  changeSubscriptionPlan,
  onChangePlanClick,
  selectedOfferId,
  setSelectedOfferId,
  isUpgradeOffer,
  setIsUpgradeOffer,
}: Props): JSX.Element => {
  const { subscription } = useAccountStore.getState();
  const { isRenewableEnabled, isCleengSubscriptionEnabled, isInplayerSubscriptionEnabled } = useAppConfig();
  const subscriptionDetailsId = useOpaqueId('subscription-details');
  const paymentMethodId = useOpaqueId('payment-method');
  const billingHistoryId = useOpaqueId('billing-history');
  const { t, i18n } = useTranslation(['user', 'account']);
  const hiddenTransactionsCount = transactions ? transactions?.length - VISIBLE_TRANSACTIONS : 0;
  const hasMoreTransactions = hiddenTransactionsCount > 0;
  const navigate = useNavigate();
  const location = useLocation();
  const isGrantedSubscription = activeSubscription?.period === 'granted';
  const breakpoint = useBreakpoint();
  const { universalStyles } = useTheme();
  const [isPaymentEdit, setIsPaymentEdit] = useState(false);

  const [isChangingOffer, setIsChangingOffer] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    if (!isChangingOffer) {
      setSelectedOfferId(activeSubscription?.accessFeeId ?? null);
    }
  }, [activeSubscription, isChangingOffer, setSelectedOfferId]);

  useEffect(() => {
    setIsChangingOffer(false);
  }, [activeSubscription?.status, activeSubscription?.pendingSwitchId]);

  // const showChangeSubscriptionButton = offerSwitchesAvailable || (!isChangingOffer && !canRenewSubscription);

  useEffect(() => {
    if (selectedOfferId && offers) {
      setIsUpgradeOffer(
        (offers.find((offer) => offer.offerId === selectedOfferId)?.customerPriceInclTax ?? 0) >
          (offers.find((offer) => offer.offerId === activeSubscription?.accessFeeId)?.customerPriceInclTax ?? 0),
      );
    }
  }, [selectedOfferId, offers, activeSubscription, setIsUpgradeOffer]);

  function onCompleteSubscriptionClick() {
    navigate(modalURLFromLocation(location, 'choose-offer'));
  }

  function onCancelSubscriptionClick() {
    navigate(modalURLFromLocation(location, 'unsubscribe'));
  }

  function onRenewSubscriptionClick() {
    navigate(modalURLFromLocation(location, 'renew-subscription'));
  }

  function getTitle(period: Subscription['period'] | undefined, title = '') {
    switch (period) {
      case 'month':
        return t('user:payment.monthly_subscription');
      case 'year':
        return t('user:payment.annual_subscription');
      case 'day':
        return t('user:payment.daily_subscription');
      case 'week':
        return t('user:payment.weekly_subscription');
      case 'granted':
        return t('user:payment.granted_subscription');
      default:
        return title;
    }
  }

  function capitalizeFirstLetter(string: string) {
    return string?.charAt(0)?.toUpperCase() + string?.slice(1);
  }

  const handleChangePlans = () => {
    navigate(modalURLFromLocation(location, 'upgrade-subscription'));
  };

  const handleCancelUpcomingPlan = () => {
    navigate(modalURLFromLocation(location, 'cancel-upcoming-subscription-plan'));
  };

  return (
    <>
      <Alert
        isSuccess={!changeSubscriptionPlan.isError}
        message={
          !changeSubscriptionPlan.isError
            ? isUpgradeOffer
              ? t('user:payment.upgrade_plan_success')
              : t('user:payment.downgrade_plan_success', {
                  date: activeSubscription?.expiresAt && formatLocalizedDate(new Date(activeSubscription.expiresAt * 1000), i18n.language),
                })
            : t('user:payment.change_plan_error')
        }
        open={isChangingOffer && (changeSubscriptionPlan.isSuccess || changeSubscriptionPlan.isError)}
        onClose={() => {
          changeSubscriptionPlan.reset();
          setIsChangingOffer(false);
        }}
      />
      <h1 className="hideUntilFocus">{t('nav.payments')}</h1>
      {((isInplayerSubscriptionEnabled && activeSubscription) || isCleengSubscriptionEnabled) && (
        <section aria-labelledby={subscriptionDetailsId} className={panelClassName}>
          <div className={`${panelHeaderClassName} ${styles.changePlanButton}`}>
            <h2 id={subscriptionDetailsId}>{isChangingOffer ? t('user:payment.change_plan') : t('user:payment.my_subscription')}</h2>
            {activeSubscription &&
              // !(pendingOffer || pendingDowngradeOfferId) &&
              (activeSubscription.status === 'active' || activeSubscription?.status === 'active_trial') && (
                <Button
                  onClick={handleChangePlans}
                  size="small"
                  label={t('user:payment.change_plan')}
                  variant="text"
                  color="primary"
                  textStyle={{ color: universalStyles.signOrSignUpLinkHighlight.color }}
                />
              )}
          </div>

          {activeSubscription ? (
            <React.Fragment>
              {!isChangingOffer && (
                <div className={styles.infoBox} key={activeSubscription.subscriptionId}>
                  <p>
                    <strong className={styles.infoBoxTitle}>
                      {getTitle(activeSubscription.period, activeSubscription.offerTitle)}
                      <span
                        className={
                          activeSubscription.status === 'active' || activeSubscription.status === 'active_trial'
                            ? styles.activeSubscriptionStatus
                            : styles.negativeSubscriptionStatus
                        }
                      >
                        {capitalizeFirstLetter(activeSubscription.status === 'active_trial' ? 'active trial' : activeSubscription.status)}
                      </span>
                    </strong>{' '}
                    <br />
                    <div className={styles.infoMainBoxDatedetails}>
                      <span className={styles.infoBoxDatedetails}>
                        {activeSubscription && (pendingOffer || pendingDowngradeOfferId) && activeSubscription.status !== 'cancelled' ? (
                          <>
                            {activeSubscription?.status === 'active' &&
                              t('user:payment.billing_ends_on', {
                                date: formatLocalizedDate(new Date(activeSubscription?.expiresAt * 1000), i18n.language),
                              })}
                          </>
                        ) : (
                          <>
                            {activeSubscription?.status === 'active' && !isGrantedSubscription && !pendingDowngradeOfferId
                              ? t('user:payment.next_billing_date_on', {
                                  date: formatLocalizedDate(new Date(activeSubscription?.expiresAt * 1000), i18n.language),
                                })
                              : t('user:payment.subscription_expires_on', {
                                  date: formatLocalizedDate(new Date(activeSubscription?.expiresAt * 1000), i18n.language),
                                })}
                          </>
                        )}
                      </span>
                      {subscription && (subscription.paymentMethod?.toLocaleLowerCase() == 'card' || subscription.paymentGateway?.toLocaleLowerCase() == 'coupon') ? (
                        <span className={styles.infoBoxDatedetails}>
                          {(activeSubscription.status === 'active' || activeSubscription.status === 'active_trial') &&
                          !isGrantedSubscription &&
                          !isChangingOffer &&
                          ((isInplayerSubscriptionEnabled) || (isCleengSubscriptionEnabled && canRenewSubscription)) &&
                          !(activeSubscription && (pendingOffer || pendingDowngradeOfferId)) ? (
                            <>
                              <span className={styles.separator} aria-hidden="true">
                                {'|'}
                              </span>
                              <span
                                className={styles.changePlanCancelButton}
                                style={{ color: universalStyles.live.backgroundColor }}
                                onClick={onCancelSubscriptionClick}
                              >
                                {t('user:payment.cancel_subscription')}
                              </span>
                            </>
                          ) : isRenewableEnabled && canRenewSubscription && activeSubscription && activeSubscription.status == 'cancelled' ? (
                            <>
                              <span className={styles.separator} aria-hidden="true">
                                {'|'}
                              </span>
                              <span className={styles.changePlanCancelButton} style={universalStyles.successColor} onClick={onRenewSubscriptionClick}>
                                {t('user:payment.renew_subscription')}
                              </span>
                            </>
                          ) : null}
                        </span>
                      ) : null}
                    </div>
                  </p>
                  {!isGrantedSubscription && (
                    <p className={styles.price}>
                      <strong>{cleengFormatPrice(activeSubscription.nextPaymentPrice, activeSubscription.nextPaymentCurrency, customer.country)}</strong>
                      <small>/{t(`account:periods.${activeSubscription.period}`, { defaultValue: activeSubscription.period })}</small>
                    </p>
                  )}
                </div>
              )}
            </React.Fragment>
          ) : isLoading ? null : (
            <React.Fragment>
              <div className={styles.noSubscription}>
                <SubscriptionBox color={universalStyles.signUpButton.backgroundColor} bg={universalStyles.modalBackgroundColors.backgroundColor} />
                <p style={universalStyles.secondaryText}>{t('user:payment.no_subscription')}</p>

                <Button variant="contained" color="primary" label={t('user:payment.complete_subscription')} onClick={onCompleteSubscriptionClick} />
              </div>
            </React.Fragment>
          )}

          {activeSubscription && (pendingOffer || pendingDowngradeOfferId) && activeSubscription.status !== 'cancelled' ? (
            <React.Fragment>
              <h1 className={styles.upcomingPlan}>{t('account:checkout.upcoming_plan')}</h1>
              {!isChangingOffer && (
                <div className={styles.infoBox} key={activeSubscription.subscriptionId}>
                  <p>
                    <strong className={styles.infoBoxTitle}>{getTitle(pendingOffer?.period ?? undefined, pendingOffer?.offerTitle)}</strong>
                    <br />
                    <div className={styles.infoMainBoxDatedetails}>
                      <span className={styles.infoBoxDatedetails}>
                        {t('user:payment.billing_starts_on', { date: formatLocalizedDate(new Date(activeSubscription?.expiresAt * 1000), i18n.language) })}
                      </span>

                      <span className={styles.infoBoxDatedetails}>
                        <span className={styles.separator} aria-hidden="true">
                          {'|'}
                        </span>
                        <span
                          className={styles.changePlanCancelButton}
                          style={{ color: universalStyles.live.backgroundColor }}
                          onClick={handleCancelUpcomingPlan}
                        >
                          {t('user:payment.cancel_plan')}
                        </span>
                      </span>
                    </div>
                  </p>

                  <p className={styles.price}>
                    <strong>
                      {pendingOffer?.customerPriceInclTax
                        ? cleengFormatPrice(pendingOffer?.customerPriceInclTax, pendingOffer?.customerCurrency, pendingOffer?.customerCountry)
                        : null}
                    </strong>
                    <small>/{t(`account:periods.${pendingOffer?.period}`, { defaultValue: pendingOffer?.period })}</small>
                  </p>
                </div>
              )}
            </React.Fragment>
          ) : null}

          {isChangingOffer && (
            <div className={styles.changePlanContainer}>
              {offers
                .filter((o) => o.planSwitchEnabled)
                .map((offer) => (
                  <OfferSwitch
                    key={offer.offerId}
                    isCurrentOffer={offer.offerId === activeSubscription?.accessFeeId}
                    pendingDowngradeOfferId={pendingDowngradeOfferId}
                    offer={offer}
                    selected={selectedOfferId === offer.offerId}
                    onChange={(offerId) => setSelectedOfferId(offerId)}
                    expiresAt={activeSubscription?.expiresAt}
                  />
                ))}
              <div className={styles.changePlanButtons}>
                <Button
                  label={t('user:account.save')}
                  onClick={onChangePlanClick}
                  disabled={selectedOfferId === activeSubscription?.accessFeeId || changeSubscriptionPlan.isLoading}
                />
                <Button label={t('user:account.cancel')} onClick={() => setIsChangingOffer(false)} variant="text" />
                {activeSubscription?.status !== 'cancelled' && (
                  <Button
                    className={styles.changePlanCancelButton}
                    label={t('user:payment.cancel_subscription')}
                    onClick={onCancelSubscriptionClick}
                    variant="danger"
                    data-testid="cancel-subscription-button"
                  />
                )}
              </div>
            </div>
          )}
          {activeSubscription && (pendingOffer || pendingDowngradeOfferId) && activeSubscription.status !== 'cancelled' && (
            <span className={styles.changePlanCancelButton} style={{ color: universalStyles.live.backgroundColor }} onClick={onCancelSubscriptionClick}>
              {t('account:cancel_subscription.unsubscribe')}
            </span>
          )}
        </section>
      )}
      <section aria-labelledby={paymentMethodId} className={panelClassName}>
        <div className={panelHeaderClassName}>
          <h2 id={paymentMethodId}>{t('user:payment.payment_method')}</h2>
        </div>
        {activePaymentDetail ? (
          activePaymentDetail.paymentMethod === 'paypal' ? (
            <div>
              <div className={styles.paypal}>{/* <Icon icon={PayPal} /> {t('account:payment.paypal')} */}</div>

              {activePaymentDetail.currency}
            </div>
          ) : (
            <div key={activePaymentDetail.id}>
              {!isPaymentEdit ? (
                <div className={styles.paymentForm}>
                  {!isInplayerSubscriptionEnabled && (
                  <div>
                    <TextField
                      label={t('user:payment.card_holder_name')}
                      name="cardSecurityCode"
                      value={activePaymentDetail.paymentMethodSpecificParams.holderName}
                      editing={isPaymentEdit}
                      autoComplete="given-name"
                      borderedReadOnlyMood={true}
                    />
                  </div>
                  )}
                  <div>
                    <TextField
                      label={t('user:payment.card_number')}
                      name="cardNumber"
                      value={`•••• •••• •••• ${activePaymentDetail.paymentMethodSpecificParams.lastCardFourDigits || ''}`}
                      aria-label={t('user:payment.card_number_hidden', { number: activePaymentDetail.paymentMethodSpecificParams.lastCardFourDigits })}
                      editing={false}
                      autoComplete="cc-number"
                      borderedReadOnlyMood={true}
                    />
                  </div>
                  <div className={styles.columns}>
                    <div>
                      <TextField
                        label={t('user:payment.expiry_date')}
                        name="cardExpiry"
                        value={activePaymentDetail.paymentMethodSpecificParams.cardExpirationDate}
                        editing={false}
                        autoComplete="cc-exp"
                        borderedReadOnlyMood={true}
                      />
                    </div>
                    <div>
                      <TextField
                        label={t('user:payment.CVV')}
                        name="cardSecurityCode"
                        value={'***'}
                        editing={false}
                        aria-label={t('user:payment.security_code_hidden')}
                        autoComplete="cc-csc"
                        borderedReadOnlyMood={true}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <EditCardDetails setIsPaymentEdit={setIsPaymentEdit} isPaymentEdit={isPaymentEdit} />
              )}
              {!isPaymentEdit && (
                <div className={styles.buttonBar}>
                  <Button
                    className={classNames({ [styles.editCard]: canUpdatePaymentMethod })}
                    label={t('account:payment.edit_card')}
                    type="button"
                    size="large"
                    variant="transparent"
                    onClick={() => setIsPaymentEdit(true)}
                    disabled={activeSubscription ? activeSubscription?.status?.toLowerCase() === 'cancelled' : false}
                  />
                </div>
              )}
            </div>
          )
        ) : (
          <div>
            <p>{!isLoading && t('user:payment.no_payment_methods')}</p>
          </div>
        )}
        {/* {canUpdatePaymentMethod && (
          <Button label={t('user:payment.update_payment_details')} type="button" onClick={() => navigate(modalURLFromLocation(location, 'payment-method'))} />
        )} */}
      </section>
      <section aria-labelledby={billingHistoryId} className={panelClassName}>
        <div className={panelHeaderClassName}>
          <h2 id={billingHistoryId}>{t('user:payment.purchase_history')}</h2>
        </div>
        {transactions?.length ? (
          <React.Fragment>
            {transactions?.slice(0, showAllTransactions ? 9999 : VISIBLE_TRANSACTIONS).map((transaction) => (
              <div className={styles.infoBox} key={transaction.transactionId}>
                <p className={styles.transactionItem}>
                  <strong className={styles.infoBoxTitle}>
                    {transaction.offerTitle}
                    <span
                      className={
                        transaction.actionType === 'active' || transaction.actionType === 'active_trial'
                          ? styles.activeSubscriptionStatus
                          : styles.negativeSubscriptionStatus
                      }
                    >
                      {/* {capitalizeFirstLetter(transaction.actionType === 'active_trial' ? 'active trial' : transaction.actionType)} */}
                    </span>
                  </strong>
                  <span className={styles.infoBoxDatedetails}>{formatLocalizedDate(new Date(transaction.transactionDate * 1000), i18n.language)}</span>
                </p>
                <div className={styles.transactionDetails}>
                  <p className={styles.price}>
                    <small> {t('user:payment.paid_with', { method: transaction.paymentMethod })} &nbsp;&nbsp;</small>
                    <strong>
                      {cleengFormatPrice(parseFloat(transaction.transactionPriceInclTax), transaction.transactionCurrency, transaction.customerCountry)}
                    </strong>
                  </p>
                </div>
              </div>
            ))}
            {!showAllTransactions && hasMoreTransactions ? (
              <React.Fragment>
                <p>{t('user:payment.hidden_transactions', { count: hiddenTransactionsCount })}</p>
                <Button label={t('user:payment.show_all')} onClick={onShowAllTransactionsClick} />
              </React.Fragment>
            ) : null}
          </React.Fragment>
        ) : (
          <div>
            <p>{!isLoading && t('user:payment.no_transactions')}</p>
          </div>
        )}
      </section>
      {isLoading && <LoadingOverlay inline />}
    </>
  );
};

export default Payment;
